import { z } from 'zod';
import { Uuid } from './primitives.js';
// Flashcards (ADR 0037, phase 1). English only; every card is approved by a person.
const Text = (max) => z.string().trim().min(1).max(max);
// ── Learner (Mini App) ─────────────────────────────────────────────────────────────────────────
export const CardsNextQuery = z.object({ deckId: Uuid.optional() }).strict();
export const CardRateRequest = z
    .object({
    cardId: Uuid,
    /** 1 Again, 2 Hard, 3 Good, 4 Easy. */
    rating: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
    /** Idempotency key (one per tap): a retried rating is applied once. */
    requestKey: z.string().min(8).max(128),
})
    .strict();
export const CardDto = z.object({
    id: Uuid,
    deckTitle: z.string(),
    front: z.string(),
    back: z.string(),
    isNew: z.boolean(),
});
export const CardsHomeDto = z.object({
    due: z.number().int(),
    newToday: z.number().int(),
    newLeft: z.number().int(),
    reviewsLeft: z.number().int(),
    full: z.boolean(),
    samplerSize: z.number().int(),
    decks: z.array(z.object({
        id: Uuid,
        title: z.string(),
        subject: z.string(),
        cards: z.number().int(),
        locked: z.boolean(),
    })),
});
export const CardsNextDto = z.object({ card: CardDto.nullable() });
export const CardRateDto = z.object({
    dueAt: z.string(),
    intervalDays: z.number(),
    replay: z.boolean(),
});
// ── Admin ──────────────────────────────────────────────────────────────────────────────────────
export const DeckStatus = z.enum(['draft', 'published', 'archived']);
export const CardStatus = z.enum(['draft', 'approved', 'rejected', 'archived']);
export const CreateDeckRequest = z
    .object({
    key: z
        .string()
        .trim()
        .regex(/^[a-z0-9][a-z0-9-]{0,47}$/),
    title: Text(80),
    subject: Text(60),
    sort: z.number().int().min(0).max(10_000).optional(),
})
    .strict();
export const UpdateDeckRequest = z
    .object({
    title: Text(80).optional(),
    subject: Text(60).optional(),
    sort: z.number().int().min(0).max(10_000).optional(),
    status: DeckStatus.optional(),
})
    .strict();
export const CardListQuery = z
    .object({
    status: CardStatus.optional(),
    deckId: Uuid.optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    offset: z.coerce.number().int().min(0).max(100_000).optional(),
})
    .strict();
export const CreateCardRequest = z
    .object({ deckId: Uuid, front: Text(500), back: Text(1000) })
    .strict();
export const EditCardRequest = z
    .object({ deckId: Uuid.optional(), front: Text(500).optional(), back: Text(1000).optional() })
    .strict()
    .refine((v) => v.deckId !== undefined || v.front !== undefined || v.back !== undefined, {
    message: 'nothing to change',
});
export const CardDecisionRequest = z
    .object({
    ids: z.array(Uuid).min(1).max(200),
    decision: z.enum(['approve', 'reject', 'archive']),
    reason: z.string().trim().max(300).optional(),
})
    .strict()
    .refine((v) => v.decision !== 'reject' || !!v.reason, {
    message: 'Say why the cards are rejected',
    path: ['reason'],
});
export const SamplerRequest = z.object({ on: z.boolean() }).strict();
export const CardImportRequest = z
    .object({
    /** The JSON body limit is 1 MB, so a file can be up to about 900 KB. */
    csv: z.string().min(1).max(900_000),
    dryRun: z.boolean(),
})
    .strict();
export const DeckDto = z.object({
    id: Uuid,
    key: z.string(),
    title: z.string(),
    subject: z.string(),
    status: DeckStatus,
    sort: z.number().int(),
    waiting: z.number().int(),
    approved: z.number().int(),
});
export const AdminCardDto = z.object({
    id: Uuid,
    deckId: Uuid,
    deckTitle: z.string(),
    front: z.string(),
    back: z.string(),
    status: CardStatus,
    sampler: z.boolean(),
    origin: z.string(),
    rejectReason: z.string().nullable(),
    createdAt: z.string(),
});
export const CardImportDto = z.object({
    dryRun: z.boolean(),
    ok: z.boolean(),
    rows: z.number().int(),
    created: z.number().int(),
    errors: z.array(z.object({ line: z.number().int(), message: z.string() })),
    preview: z.array(z.object({ line: z.number().int(), deck: z.string(), front: z.string(), back: z.string() })),
});
//# sourceMappingURL=flashcards.js.map